/**
 * Field resolution for article-template components (ArticleHeroSection,
 * ArticleBreadcrumb, ArticleAuthorBlock) — binds HeroFieldSlot/BreadcrumbItem
 * `apiField` paths against the Quintype `story` object.
 *
 * Path algorithm mirrors the page-builder's (unexported) resolveApiFieldPath
 * in src/components/page-builder/ComponentRenderer.tsx, scoped to a raw
 * `story` object instead of a TransformedNewsItem.
 */

import { AuthorCardItem, BreadcrumbItem, GalleryItem, HeroField, HeroImageMediaMode } from './types/articleTemplate';
import { getImageUrl } from '@/lib/api';
import { getTimeAgoMeta } from '@/lib/componentutills';

/**
 * Slots carrying a `dateFormat` (e.g. "ta-IN-ist") are bound to a raw
 * Quintype timestamp (milliseconds, e.g. story['published-at']) rather than
 * display-ready text — without this, the slot would render the literal
 * number. getTimeAgo() already produces Tamil-locale, IST-formatted output
 * (relative for recent items, absolute date otherwise), matching every other
 * date shown on the article page, so any dateFormat value routes through it.
 */
function formatDateSlotValue(
  rawValue: unknown,
  dateFormat: string | undefined
): { text: string; iso: string; title: string } | null {
  if (!dateFormat) return null;
  const numeric =
    typeof rawValue === 'number' ? rawValue
      : typeof rawValue === 'string' && /^\d+$/.test(rawValue) ? Number(rawValue)
        : null;
  if (numeric === null) return null;
  return getTimeAgoMeta(numeric);
}

/**
 * Image slots bind to raw Quintype S3 keys (e.g. "dinamani/import/2023/2/2/
 * original/PAN-Card-Mistakes.jpg"), not resolvable URLs — resolve through
 * getImageUrl() unless the value is already a full URL (a static/manually
 * entered image field).
 */
function resolveImageUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  return /^https?:\/\//.test(value) ? value : getImageUrl(value);
}

/**
 * Resolve a dotted/bracket path against the story object.
 *
 * Builder-authored apiFields are rooted at "story." (matching the raw
 * Quintype API response shape `{ story: {...} }`, the same wrapper
 * getArticleBySlug() destructures `story` out of) — so paths are resolved
 * against `{ story }`, not the bare story object.
 *
 * Builder-authored paths also often omit explicit array indices for
 * "first item" access (e.g. "story.sections.name" rather than
 * "story.sections[0].name" or "story.sections[].name") — so when traversal
 * hits an array and the next segment isn't a numeric index, it implicitly
 * descends into index 0.
 *
 * - "story.sections.name" -> story.sections[0].name (implicit array index)
 * - "story.tags[].name" -> story.tags[0].name ([] treated as [0])
 * - "story.sections[0].name" -> story.sections[0].name
 * - "story.hero-image-s3-key" -> story['hero-image-s3-key']
 */
export function resolveStoryField(story: Record<string, unknown> | null | undefined, apiField?: string | null): unknown {
  if (!story || !apiField) return undefined;

  const normalizedPath = apiField.replace(/\[\]/g, '[0]');
  const parts = normalizedPath.replace(/\[(\d+)\]/g, '.$1').split('.');

  let value: unknown = { story };
  for (const part of parts) {
    if (value == null) return undefined;
    if (Array.isArray(value) && !/^\d+$/.test(part)) {
      value = value[0];
      if (value == null) return undefined;
    }
    value = (value as Record<string, unknown>)[part];
  }
  return value;
}

/**
 * Resolve a slot's display value: static text is used only for genuinely
 * static fields (no apiField configured at all) — e.g. a fixed color or a
 * hand-typed label. When an apiField IS configured but resolves to nothing
 * (and apiFieldFallback, if any, also resolves to nothing), this returns ''
 * rather than falling back to `slot.static`, which is the Builder authoring
 * UI's placeholder/example copy ("Article headline goes here", "By Author
 * Name", ...) — meant to guide template authors while editing, not to be
 * shown to real readers on the live page when a bound field has no data.
 * Every field-rendering component already treats '' as "hide this field",
 * so this fix cascades correctly without touching the renderers themselves.
 */
/**
 * Full resolution of a slot, including datetime metadata (ISO 8601 +
 * "YYYY-MM-DD HH:mm" title) when the slot is date-formatted — used to build
 * a semantic <time datetime=".." title=".."> for SERP-readable dates.
 * resolveSlotValue (below) is the plain-text-only view most callers want.
 */
function resolveSlotValueMeta(
  story: Record<string, unknown> | null | undefined,
  slot: { static?: string; apiField?: string; apiFieldFallback?: string; dateFormat?: string }
): { text: string; iso?: string; title?: string } {
  if (slot.apiField) {
    const value = resolveStoryField(story, slot.apiField);
    if (value !== undefined && value !== null && value !== '') {
      const dateMeta = formatDateSlotValue(value, slot.dateFormat);
      return dateMeta ?? { text: String(value) };
    }
    if (slot.apiFieldFallback) {
      const fallback = resolveStoryField(story, slot.apiFieldFallback);
      if (fallback !== undefined && fallback !== null && fallback !== '') {
        const dateMeta = formatDateSlotValue(fallback, slot.dateFormat);
        return dateMeta ?? { text: String(fallback) };
      }
    }
    return { text: '' };
  }
  return { text: slot.static ?? '' };
}

export function resolveSlotValue(
  story: Record<string, unknown> | null | undefined,
  slot: { static?: string; apiField?: string; apiFieldFallback?: string; dateFormat?: string }
): string {
  return resolveSlotValueMeta(story, slot).text;
}

export function resolveBreadcrumbItems(items: BreadcrumbItem[] | undefined, story: Record<string, unknown> | null | undefined): BreadcrumbItem[] {
  if (!items) return [];
  return items
    .filter(item => !item.hidden)
    .map(item => {
      if (!item.apiField) return item;
      const resolved = resolveStoryField(story, item.apiField);
      if (resolved === undefined || resolved === null || resolved === '') return { ...item, label: '' };
      return { ...item, label: String(resolved) };
    })
    // An apiField-bound item that resolved to nothing has no real label to
    // show — dropping it (rather than rendering an empty "By" / breadcrumb
    // slash) matches resolveSlotValue's "hide, don't show placeholder copy"
    // behavior above.
    .filter(item => !item.apiField || !!item.label);
}

export function resolveAuthorItems(items: BreadcrumbItem[] | undefined, story: Record<string, unknown> | null | undefined): BreadcrumbItem[] {
  return resolveBreadcrumbItems(items, story)
    .filter(item => {
      // 'html'/'image' items carry their own content, independent of any
      // apiField binding — keep them whenever they actually have that content.
      if (item.renderMode === 'html') return !!item.customHtml;
      if (item.renderMode === 'image') return !!item.customImageUrl;
      // Unlike breadcrumb (where a static, unbound level like "முகப்பு" home
      // is legitimate real content), a static default-mode author item is
      // always the Builder's unedited "Author Name" placeholder scaffold —
      // template authors bind authorItems to a real apiField (e.g.
      // story.authors) to show real names; if a template never did that,
      // there's no real author to show, and "By Author Name" showing on the
      // live page is placeholder copy leaking through, not intentional
      // content (see resolveSlotValue's rationale above).
      return !!item.apiField;
    })
    .map(item => {
      if (!item.apiField || !item.apiField.endsWith('.name')) return item;
      // resolveBreadcrumbItems only re-resolves `label`; `url` is left at
      // whatever was configured (the Builder's static "#" default), so a
      // bound author name renders as a dead link. Every other author link
      // in this codebase points to /author/{slug} — mirror that by
      // resolving the sibling `slug` field at the same object path (e.g.
      // "story.authors.name" -> "story.authors.slug", also correct for
      // "story.authors[0].name"/"story.authors[].name" variants, since
      // `.name` is always the path's last segment).
      const slugField = item.apiField.replace(/\.name$/, '.slug');
      const slug = resolveStoryField(story, slugField);
      return typeof slug === 'string' && slug ? { ...item, url: `/author/${slug}` } : item;
    });
}

export interface ResolvedAuthorCard {
  id: string;
  prefixLabel?: string;
  label: string;
  url: string;
  profileImage?: string;
  showProfile: boolean;
  showName: boolean;
  showSocial: boolean;
  socialLinks: AuthorCardItem['socialLinks'];
  labelStyle?: AuthorCardItem['labelStyle'];
  nameStyle?: AuthorCardItem['nameStyle'];
}

/**
 * Resolve a rich AuthorCardItem (the Hero Section's `author`/`guestAuthor`)
 * against the story: label via apiField (falling back to the static label),
 * url via urlApiField (falling back to the same `.name`->`.slug` convention
 * resolveAuthorItems uses, then the static url), profile image via
 * profileApiField (falling back to the static profileImage, resolved through
 * getImageUrl like every other image slot). socialLinks are never resolved
 * dynamically (see AuthorCardItem.socialApiField) — always the static list.
 */
export function resolveAuthorCard(card: AuthorCardItem | undefined, story: Record<string, unknown> | null | undefined): ResolvedAuthorCard | null {
  if (!card || card.hidden) return null;

  const label = card.apiField
    ? (resolveStoryField(story, card.apiField) as string | undefined) || ''
    : card.label || '';

  let url = card.urlApiField
    ? (resolveStoryField(story, card.urlApiField) as string | undefined) || ''
    : '';
  // A urlApiField bound to a `.slug`-style field (e.g. "story.authors.slug")
  // resolves to a bare slug, not a navigable path — every other author link
  // in this codebase points to /author/{slug}, so treat a resolved value with
  // no leading slash and no scheme (not already a path or an external URL)
  // the same way.
  if (url && !url.startsWith('/') && !/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    url = `/author/${url}`;
  }
  if (!url && card.apiField?.endsWith('.name')) {
    const slug = resolveStoryField(story, card.apiField.replace(/\.name$/, '.slug'));
    if (typeof slug === 'string' && slug) url = `/author/${slug}`;
  }
  if (!url) url = card.url || '';

  const profileImage = card.profileApiField
    ? resolveImageUrl(resolveStoryField(story, card.profileApiField) as string | undefined)
    : resolveImageUrl(card.profileImage);

  if (!label && !profileImage) return null;

  return {
    id: card.id,
    prefixLabel: card.prefixLabel,
    label,
    url,
    profileImage,
    showProfile: card.showProfile !== false,
    showName: card.showName !== false && !!label,
    showSocial: !!card.showSocial,
    socialLinks: (card.socialLinks || []).filter(s => !s.hidden),
    labelStyle: card.labelStyle,
    nameStyle: card.nameStyle,
  };
}

export function resolveGalleryItems(items: GalleryItem[] | undefined, story: Record<string, unknown> | null | undefined): GalleryItem[] {
  if (!items) return [];
  return items
    .filter(item => !item.hidden)
    .map(item => {
      const imageSrc = item.imageApiField ? (resolveStoryField(story, item.imageApiField) as string | undefined) : undefined;
      const caption = item.captionApiField ? (resolveStoryField(story, item.captionApiField) as string | undefined) : undefined;
      return {
        ...item,
        imageSrc: resolveImageUrl(imageSrc || item.imageSrc) || '',
        caption: caption || item.caption,
      };
    })
    // A gallery item with no bound apiField and no static image (an empty
    // placeholder left over from authoring) has nothing to show — rendering
    // it produces an <img> with an empty src (a broken-image box), so drop it.
    .filter(item => !!item.imageSrc);
}

export interface ResolvedHeroMedia {
  mode: HeroImageMediaMode;
  imageSrc?: string;
  imageAlt?: string;
  caption?: string;
  attribution?: string;
  videoPreviewImage?: string;
  videoUrl?: string;
  gumletVideoUrl?: string;
  /** Raw base64-encoded Gumlet embed script (story['embed-js'] or the first
   *  card's jsembed element) — a fallback source for gumletVideo mode when
   *  no clean `gumletVideoUrl` slot is bound (see resolveGumletEmbedJs). */
  embedJs?: string;
  galleryItems?: GalleryItem[];
}

function slotValue(field: HeroField, key: string, story: Record<string, unknown> | null | undefined): string | undefined {
  const slot = field.slots?.find(s => s.key === key);
  if (!slot || slot.hidden) return undefined;
  const value = resolveSlotValue(story, slot);
  return value || undefined;
}

function slotValueMeta(
  field: HeroField,
  key: string,
  story: Record<string, unknown> | null | undefined
): { text: string; iso?: string; title?: string } | undefined {
  const slot = field.slots?.find(s => s.key === key);
  if (!slot || slot.hidden) return undefined;
  const meta = resolveSlotValueMeta(story, slot);
  return meta.text ? meta : undefined;
}

/**
 * Video/gumletVideo modes expect a dedicated `videoPreviewImage` slot, but a
 * template author can switch a hero field's Media Type to Video/Gumlet Video
 * without the Builder repopulating that slot (leaving only the old image-mode
 * `imageSrc` slot, or nothing at all) — which previously rendered a fully
 * blank hero. Fall back to the field's own `imageSrc` slot, then to the
 * story's own hero image, so there's always something to show.
 */
function resolveVideoPreviewImage(field: HeroField, story: Record<string, unknown> | null | undefined): string | undefined {
  return (
    resolveImageUrl(slotValue(field, 'videoPreviewImage', story))
    || resolveImageUrl(slotValue(field, 'imageSrc', story))
    || resolveImageUrl(story?.['hero-image-s3-key'] as string | undefined)
  );
}

/**
 * gumletVideo mode's `gumletVideoUrl` slot is often left unbound (the
 * Builder doesn't always repopulate it when a template's Media Type is
 * switched to Gumlet Video) — in practice the actual video lives on the raw
 * Quintype story instead, as a base64-encoded embed script: either
 * story['embed-js'] directly, or the first jsembed element found in the
 * first card. Same fallback pattern as resolveVideoPreviewImage.
 */
function resolveGumletEmbedJs(story: Record<string, unknown> | null | undefined): string | undefined {
  const storyEmbedJs = story?.['embed-js'] as string | undefined;
  if (storyEmbedJs) return storyEmbedJs;
  const cards = story?.['cards'] as any[] | undefined;
  const firstCardEmbed = cards?.[0]?.['story-elements']?.find((el: any) => el?.type === 'jsembed' && el?.['embed-js']);
  return firstCardEmbed?.['embed-js'];
}

/**
 * Mirrors BigNewsVideo's own resolution: story['embed-url'] is often empty,
 * with the actual YouTube URL living on a youtube-video element in the first
 * card instead. Same fallback pattern as resolveGumletEmbedJs.
 */
function resolveVideoEmbedUrlFromStory(story: Record<string, unknown> | null | undefined): string | undefined {
  const storyEmbedUrl = story?.['embed-url'] as string | undefined;
  if (storyEmbedUrl) return storyEmbedUrl;
  const cards = story?.['cards'] as any[] | undefined;
  const firstCardEmbed = cards?.[0]?.['story-elements']?.find((el: any) => el?.['embed-url']);
  return firstCardEmbed?.['embed-url'];
}

/**
 * Resolve the heroImage field's media, switching on mediaMode (default
 * 'image'). `templateType` overrides the default: a `video` template's
 * heroImage field is frequently left at the Builder's default Media Type
 * (plain 'image') rather than explicitly switched to "Video" — treating it
 * as video mode anyway (with videoUrl resolved from the story itself, same
 * as gumletVideo's embedJs fallback) means the actual playable video renders
 * in the hero's proper position instead of only in the body.
 */
export function resolveHeroMedia(
  field: HeroField,
  story: Record<string, unknown> | null | undefined,
  templateType?: string
): ResolvedHeroMedia {
  const mode: HeroImageMediaMode = field.mediaMode || (templateType === 'video' ? 'video' : 'image');
  const caption = slotValue(field, 'caption', story);
  const attribution = slotValue(field, 'attribution', story);

  if (mode === 'photoGallery') {
    const galleryItems = resolveGalleryItems(field.galleryItems, story);
    // No usable photos configured (empty placeholder items authored with no
    // apiField/static image) — fall back to the story's own hero image
    // rather than an empty gallery strip.
    if (galleryItems.length === 0) {
      const fallbackImageSrc = resolveImageUrl(story?.['hero-image-s3-key'] as string | undefined);
      if (fallbackImageSrc) {
        return { mode: 'image', imageSrc: fallbackImageSrc, caption, attribution };
      }
    }
    return { mode, galleryItems };
  }

  if (mode === 'video') {
    return {
      mode,
      videoPreviewImage: resolveVideoPreviewImage(field, story),
      videoUrl: slotValue(field, 'videoUrl', story) || resolveVideoEmbedUrlFromStory(story),
      caption,
      attribution,
    };
  }

  if (mode === 'gumletVideo') {
    const gumletVideoUrl = slotValue(field, 'gumletVideoUrl', story);
    return {
      mode,
      videoPreviewImage: resolveVideoPreviewImage(field, story),
      gumletVideoUrl,
      embedJs: gumletVideoUrl ? undefined : resolveGumletEmbedJs(story),
      caption,
      attribution,
    };
  }

  return {
    mode: 'image',
    imageSrc: resolveImageUrl(slotValue(field, 'imageSrc', story)),
    imageAlt: slotValue(field, 'imageAlt', story),
    caption,
    attribution,
  };
}

/**
 * Resolve the dateMeta field's two label/value rows in configured pairOrder.
 */
export function resolveDateMeta(
  field: HeroField,
  story: Record<string, unknown> | null | undefined
): Array<{ key: string; label: string; value: string; iso?: string; title?: string }> {
  const created = slotValueMeta(field, 'createdAt', story);
  const updated = slotValueMeta(field, 'updatedAt', story);
  const rows: Record<string, { key: string; label: string; value: string; iso?: string; title?: string }> = {
    createdLabel: {
      key: 'createdLabel',
      label: slotValue(field, 'createdLabel', story) || 'வெளியிடப்பட்டது',
      value: created?.text || '',
      iso: created?.iso,
      title: created?.title,
    },
    updatedLabel: {
      key: 'updatedLabel',
      label: slotValue(field, 'updatedLabel', story) || 'புதுப்பிக்கப்பட்டது',
      value: updated?.text || '',
      iso: updated?.iso,
      title: updated?.title,
    },
  };
  const order = field.pairOrder && field.pairOrder.length === 2 ? field.pairOrder : ['createdLabel', 'updatedLabel'];
  return order.map(key => rows[key]).filter(Boolean);
}
